// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// "Start here" — the proportional-area explainer.
//
// ONE GRID, ONE DENOMINATOR. A thousand squares stand for Australia's land
// area, and the six steps re-partition the SAME thousand squares. The grid
// never resizes and the denominator never changes, so the reader holds one
// whole in their eye from the first step to the last: every later percentage on
// this site is a share of the block they are looking at here.
//
// Two things this view is careful about, because both are easy to draw wrongly:
//
//   * The four outcome categories DO NOT PARTITION AUSTRALIA. "Does not exist"
//     and "extinguished" are separate findings about specific pieces of ground —
//     frequently pieces inside the very same determination as recognised
//     country — not the remainder of the continent. So they are drawn as their
//     own block, with a visible gap, and the caption says so in words.
//
//   * Every square count is derived from meta.json at runtime. Nothing here is
//     a hard-coded square count, so a rebuild that moves the areas moves the
//     picture with it rather than quietly leaving it wrong.

import { TENURE, TENURE_ORDER, ALRA_CAVEAT, mixHex, type Tenure } from '../theme';
import { el, chart, defs, tenureFill, tenureHue } from '../components/svg';
import { esc, num, km2, pct, longDate, tip, tipAttr } from '../format';
import { glossTerm, glossIcon } from '../components/glossary';
import { openOverlay, closeButton } from '../overlay';
import { navigate } from '../router';
import { attachSvgZoom } from '../utils/svgZoom';
import { getMeta, getDeterminations, loadJurisdictions } from '../data';

/* ───────────────────────────── grid geometry ──────────────────────────────
 * 40 x 25 = 1,000 squares. The SVG carries no text at all: the numbers live in
 * the HTML legend and caption underneath, so the box can scale down to a phone
 * without dragging a label with it. */
const COLS = 40;
const ROWS = 25;
const TOTAL_SQUARES = COLS * ROWS;
const CELL = 20;
const GAP = 2;
const PITCH = CELL + GAP;
const PAD = 4;
const GRID_W = PAD * 2 + COLS * PITCH - GAP;
const GRID_H = PAD * 2 + ROWS * PITCH - GAP;

const NEUTRAL_FILL = '#eae4d6';
const NEUTRAL_STROKE = 'rgba(26,24,21,.10)';
const BAND_INK = '#47433b';

/** The one hue that means "recognised" before step 4 splits it in two. */
const RECOGNITION_HUE = mixHex(TENURE.exclusive.fill, TENURE.nonexclusive.fill, 0.5);

type Cat = Tenure | 'recognition' | 'rest';

interface Paint {
  fill: string;
  stroke: string;
  tip: string;
  swatch: string;
  label: string;
}

/** Each outcome category's glossary entry. */
const GLOSS_KEY: Record<Tenure, string> = {
  exclusive: 'exclusive-possession',
  nonexclusive: 'non-exclusive',
  notexist: 'does-not-exist',
  extinguished: 'extinguishment',
};

const STEP_TITLES = [
  'One continent, a thousand squares',
  '683 determinations — which is not the same as 683 recognitions',
  'Recognised',
  'Of what was recognised, how much is exclusive',
  'Two more findings, and they are not the remainder',
  'What these registers do not measure at all',
];

export async function renderStart(host: HTMLElement): Promise<void> {
  const meta = getMeta();
  const dets = getDeterminations();
  const jurisdictions = await loadJurisdictions();

  /* ─────────────────────────── the arithmetic ─────────────────────────────
   * Every square count below comes from meta.json. The recognised block is
   * split so that exclusive + non-exclusive is EXACTLY the recognised count —
   * rounding each of the two independently would have produced 424 squares of
   * recognition inside a 423-square block, and the grid would have lied by one
   * square about the only number the whole view exists to show. */
  const unitKm2 = meta.australiaLandKm2 / TOTAL_SQUARES;
  const squaresFor = (km: number) => Math.round((km / meta.australiaLandKm2) * TOTAL_SQUARES);

  const recSq = Math.round(meta.areas.recognisedPct * 10);
  const exSq = Math.round(meta.areas.exclusivePct * 10);
  const nonExSq = recSq - exSq;
  const notExistSq = squaresFor(meta.areas.notExistKm2);
  const extSq = squaresFor(meta.areas.extinguishedKm2);
  const restSq = TOTAL_SQUARES - recSq - notExistSq - extSq;

  const nonExPct = (meta.areas.nonExclusiveKm2 / meta.australiaLandKm2) * 100;
  const notExistPct = (meta.areas.notExistKm2 / meta.australiaLandKm2) * 100;
  const extPct = (meta.areas.extinguishedKm2 / meta.australiaLandKm2) * 100;
  const nonExShareOfRec = (meta.areas.nonExclusiveKm2 / meta.areas.recognisedKm2) * 100;
  const restKm2 = meta.australiaLandKm2
    - meta.areas.recognisedKm2 - meta.areas.notExistKm2 - meta.areas.extinguishedKm2;

  const noneCount = dets.filter((d) => d.finding === 'none').length;
  const nonePct = (noneCount / meta.counts.determinations) * 100;
  const consentCount = dets.filter((d) => d.method === 'consent').length;
  const consentPct = (consentCount / meta.counts.determinations) * 100;

  const actMultiple = unitKm2 / meta.landAreaKm2.ACT;

  // The spread of determination AREAS, straight from the register — the point
  // being that counting determinations says nothing about ground covered.
  const landAreas = dets.filter((d) => !d.sea && d.areaSqKm != null).map((d) => d.areaSqKm as number);
  const smallestArea = landAreas.length ? Math.min(...landAreas) : 0;
  const largestArea = landAreas.length ? Math.max(...landAreas) : 0;
  const smallestLabel = smallestArea < 1
    ? `${(smallestArea * 100).toFixed(2)} hectares`
    : km2(smallestArea, { exact: true });

  // The agreements register is the one that double-counts badly; the outcome
  // polygons on this grid barely overlap on land. Both figures are read from
  // meta rather than asserted.
  const agreementRatio = meta.areas.agreementNaiveSumKm2 / meta.areas.agreementUnionKm2;
  const worstOverlap = [...meta.areas.overlap].sort((a, b) => b.overlapPct - a.overlapPct)[0];
  const worstOverlapLabel = TENURE[worstOverlap.tenure as Tenure]?.label ?? worstOverlap.tenure;

  // Roughly half the Northern Territory, expressed in squares of this grid.
  const halfNtKm2 = meta.landAreaKm2.NT * 0.5;
  const bandSquares = Math.round(halfNtKm2 / unitKm2);
  const nt = jurisdictions.find((j) => j.code === 'NT');
  const zeroJur = jurisdictions.filter((j) => j.determinations === 0);

  /* ─────────────────────────────── paints ───────────────────────────────── */
  const areaLine = (km: number) => `Area: ${km2(km, { exact: true })} — a dissolved union, land only`;
  const perSquare = `One square = ${km2(unitKm2)} of the ${num(meta.australiaLandKm2)} km² of Australian land`;

  const PAINT: Record<Cat, Paint> = {
    exclusive: {
      fill: tenureFill('exclusive'),
      stroke: 'none',
      swatch: `background:${tenureHue('exclusive')}`,
      label: TENURE.exclusive.label,
      tip: tip(
        `${TENURE.exclusive.label} — ${num(exSq)} of 1,000 squares`,
        TENURE.exclusive.plain,
        areaLine(meta.areas.exclusiveKm2),
        `Share of Australia’s land: ${pct(meta.areas.exclusivePct)}`,
        `Share of the recognised area: ${pct(meta.areas.exclusiveShareOfRecognised)}`,
        perSquare,
        'Click to open the map.',
      ),
    },
    nonexclusive: {
      fill: tenureFill('nonexclusive'),
      stroke: 'none',
      swatch: `background:${tenureHue('nonexclusive')};background-image:radial-gradient(rgba(255,255,255,.78) 1px, transparent 1px);background-size:4px 4px`,
      label: TENURE.nonexclusive.label,
      tip: tip(
        `${TENURE.nonexclusive.label} — ${num(nonExSq)} of 1,000 squares`,
        TENURE.nonexclusive.plain,
        areaLine(meta.areas.nonExclusiveKm2),
        `Share of Australia’s land: ${pct(nonExPct)}`,
        `Share of the recognised area: ${pct(nonExShareOfRec)}`,
        perSquare,
        'Click to open the map.',
      ),
    },
    notexist: {
      fill: tenureFill('notexist'),
      stroke: 'none',
      swatch: `background:${tenureHue('notexist')}`,
      label: TENURE.notexist.label,
      tip: tip(
        `${TENURE.notexist.label} — ${num(notExistSq)} of 1,000 squares`,
        TENURE.notexist.plain,
        areaLine(meta.areas.notExistKm2),
        `Share of Australia’s land: ${pct(notExistPct)}`,
        'Not part of the recognised area — a separate finding about particular ground, often ground inside the same determination.',
        perSquare,
        'Click to open the map.',
      ),
    },
    extinguished: {
      fill: tenureFill('extinguished'),
      stroke: 'none',
      swatch: `background:${tenureHue('extinguished')};background-image:repeating-linear-gradient(45deg, rgba(255,255,255,.7) 0 2px, transparent 2px 4px)`,
      label: TENURE.extinguished.label,
      tip: tip(
        `${TENURE.extinguished.label} — ${num(extSq)} of 1,000 squares`,
        TENURE.extinguished.plain,
        areaLine(meta.areas.extinguishedKm2),
        `Share of Australia’s land: ${pct(extPct)}`,
        'Not part of the recognised area — a separate finding about particular ground.',
        perSquare,
        'Click to open the map.',
      ),
    },
    recognition: {
      fill: RECOGNITION_HUE,
      stroke: 'none',
      swatch: `background:${RECOGNITION_HUE}`,
      label: 'Native title recognised',
      tip: tip(
        `Native title recognised — ${num(recSq)} of 1,000 squares`,
        'The Federal Court has recognised native title over this ground: exclusive possession in some places, rights that coexist with someone else’s interest in others. The next step splits it.',
        areaLine(meta.areas.recognisedKm2),
        `Share of Australia’s land: ${pct(meta.areas.recognisedPct)}`,
        perSquare,
        'Click to open the map.',
      ),
    },
    rest: {
      fill: NEUTRAL_FILL,
      stroke: NEUTRAL_STROKE,
      swatch: `background:${NEUTRAL_FILL};border-color:rgba(26,24,21,.18)`,
      label: 'No outcome on this register',
      tip: '',
    },
  };

  /**
   * The pale squares mean a different thing at each stage, and saying the wrong
   * one is a real error rather than a wording preference: at step 3 they are
   * "everything outside the recognised block", which still contains the ground
   * the next step but one draws as its own findings. Only from step 5, once
   * those are on the grid, are the pale squares ground no outcome polygon
   * touches.
   */
  const restInfo = (step: number): { label: string; tip: string } => {
    if (step <= 2) {
      return {
        label: 'Australia’s land area',
        tip: tip(
          'Australia’s land area',
          `${num(meta.australiaLandKm2)} km², as published by Geoscience Australia`,
          `1,000 squares · ${perSquare}`,
          'Sea country sits outside this denominator, and outside every percentage on this site.',
        ),
      };
    }
    if (step <= 4) {
      return {
        label: 'Outside the recognised block',
        tip: tip(
          'Outside the recognised block',
          `${num(TOTAL_SQUARES - recSq)} of 1,000 squares · about ${km2(meta.australiaLandKm2 - meta.areas.recognisedKm2)}`,
          'Not one thing. Some of it carries other findings, which the next steps draw; most of it is ground these registers say nothing about at all.',
          perSquare,
        ),
      };
    }
    return {
      label: 'No outcome on this register',
      tip: tip(
        'No outcome polygon on these registers covers this ground',
        `${num(restSq)} of 1,000 squares · about ${km2(restKm2)}`,
        'Either no determination has been made over it, or it was never part of one. It is not a category a court has found — it is ground these registers say nothing about.',
        perSquare,
      ),
    };
  };

  /* ──────────────────────────── the page shell ──────────────────────────── */
  const root = document.createElement('div');
  root.className = 'stack';
  root.innerHTML = `
    <div class="view-head">
      <h1>Start here</h1>
      <p class="lede">Australia has ${esc(num(meta.australiaLandKm2))} km² of land. The grid below is that
        land, cut into a thousand equal squares — one square is ${esc(km2(unitKm2))}, a little over
        ${esc(actMultiple.toFixed(1))} times the area of the Australian Capital Territory. Six steps
        re-partition the same thousand squares. The grid never resizes and the denominator never changes.</p>
    </div>

    <section class="card start-stepper" aria-label="A six-step explainer built on one fixed grid">
      <div class="start-step-head">
        <div class="start-dots" id="start-dots"></div>
        <span class="start-step-count mono" id="start-step-count"></span>
      </div>

      <div class="chart-frame start-grid-frame" id="start-grid-frame"></div>

      <div class="legend start-legend" id="start-legend"></div>

      <div class="start-caption" id="start-caption" aria-live="polite"></div>

      <div class="start-nav">
        <button type="button" class="btn btn-sm" id="start-prev">← Previous</button>
        <button type="button" class="btn btn-sm" id="start-next">Next →</button>
        <span class="start-nav-spacer"></span>
        <button type="button" class="btn btn-sm" id="start-to-map">Skip to the map →</button>
      </div>
      <p class="xsmall muted">Use the ← and → arrow keys to move between steps. Hover any square for its
        exact area; click a filled block to open the map.</p>

      <div class="start-notes">
        <div class="note">
          <strong>Every area here is measured once.</strong> Where two boundaries overlap, adding their
          areas counts the same country twice, so every figure on this site is a
          ${glossTerm('dissolved-union')} in an ${glossTerm('equal-area-projection')}, land only —
          never a sum of a register’s own area column. The agreements register shows why that matters:
          its own area column adds up to ${esc(num(meta.areas.agreementNaiveSumKm2))} km², while the
          ground those ${esc(num(meta.counts.agreements))} agreements actually cover is
          ${esc(num(meta.areas.agreementUnionKm2))} km². The column is ${esc(agreementRatio.toFixed(1))} times
          the ground, because ${esc(pct(meta.areas.agreementOverlapPct, 0))} of it is the same country counted
          more than once. The determination outcomes drawn on this grid are different: on land they barely
          overlap at all, and the largest double count in any of the four categories is
          ${esc(pct(worstOverlap.overlapPct))}, on “${esc(worstOverlapLabel)}”.
        </div>

        <div class="note warn">
          <strong>A ${glossTerm('determination')} is not automatically a recognition.</strong>
          ${esc(num(noneCount))} of the ${esc(num(meta.counts.determinations))} determinations on the
          register — ${esc(pct(nonePct))} — found that ${glossTerm('does-not-exist', 'native title does not exist')}
          in the area the court considered. That is a finding about ground, on the evidence before the
          court, and this site never describes it as anything else.
        </div>

        <div class="note">
          <strong>The Northern Territory is not what it looks like here.</strong>
          Roughly half the Territory is Aboriginal freehold under the
          ${glossTerm('alra', 'Aboriginal Land Rights (Northern Territory) Act 1976')} — a different
          statute these registers do not cover at all.
          <button type="button" class="btn btn-sm start-note-btn" id="start-alra-note">Read the full caveat</button>
        </div>

        <div class="note">
          <strong>${esc(zeroJur.length === 1 ? 'One jurisdiction is' : zeroJur.length === 2 ? 'Two jurisdictions are' : `${num(zeroJur.length)} jurisdictions are`)} a real zero.</strong>
          ${esc(zeroJur.map((j) => (j.name.includes('Territory') ? `the ${j.name}` : j.name)).join(' and '))}
          ${zeroJur.length === 1 ? 'has' : 'have'} no determination at all —
          <span class="mono">0</span> of ${esc(num(meta.counts.determinations))}. That is a fact recorded by
          the register, not missing data, and it is drawn as a zero rather than left blank.
          <button type="button" class="btn btn-sm start-note-btn" id="start-to-jur">See it state by state</button>
        </div>
      </div>
    </section>

    <section class="start-words" aria-labelledby="start-words-h">
      <h2 id="start-words-h" class="card-title">What the four words mean${glossIcon('determination')}</h2>
      <p class="small muted">A single ${glossTerm('determination-area', 'determination area')} is usually a
        mosaic of these, not one of them. They are always shown in this order, and each one is a hue and a
        texture together, so none of them depends on colour alone.</p>
      <div class="legend-cards start-word-cards" id="start-words"></div>
    </section>
  `;

  /* ───────────────────────────── the grid SVG ───────────────────────────── */
  const frame = root.querySelector<HTMLElement>('#start-grid-frame')!;
  const svg = chart(GRID_W, GRID_H, `One thousand squares, one for each ${km2(unitKm2)} of Australia’s land area`);
  defs(svg);

  const cellLayer = el('g');
  const cells: SVGRectElement[] = [];
  for (let i = 0; i < TOTAL_SQUARES; i++) {
    const r = el('rect', {
      x: PAD + (i % COLS) * PITCH,
      y: PAD + Math.floor(i / COLS) * PITCH,
      width: CELL,
      height: CELL,
      rx: 2,
      class: 'start-cell',
    });
    cellLayer.append(r);
    cells.push(r);
  }
  svg.append(cellLayer);

  // Where the "does not exist" / "extinguished" block starts: the next whole
  // row after the recognised block, plus one blank row, so the gap between the
  // two blocks is unmistakable.
  const SEPARATE_START = (Math.ceil(recSq / COLS) + 1) * COLS;
  const sepY = PAD + (Math.ceil(recSq / COLS) + 0.5) * PITCH - GAP / 2;
  const separator = el('line', {
    x1: PAD, x2: PAD + COLS * PITCH - GAP, y1: sepY, y2: sepY,
    stroke: BAND_INK, 'stroke-width': 1.6, 'stroke-dasharray': '5 6', opacity: 0.55,
    'aria-hidden': 'true',
  });
  svg.append(separator);

  const bandH = (bandSquares / COLS) * PITCH;
  const bandY = PAD + 20 * PITCH - GAP / 2;
  const band = el('g', {
    class: 'start-band hit',
    role: 'button',
    tabindex: '0',
    'aria-label': 'The Aboriginal Land Rights (Northern Territory) Act 1976 — what these registers do not cover. Opens an explanation.',
  });
  band.append(el('rect', {
    x: PAD - 2, y: bandY, width: COLS * PITCH - GAP + 4, height: bandH, rx: 4,
    fill: 'rgba(26,24,21,.07)', stroke: BAND_INK, 'stroke-width': 2, 'stroke-dasharray': '8 5',
  }));
  band.setAttribute('data-tip', tip(
    'Aboriginal Land Rights (Northern Territory) Act 1976',
    `About ${km2(halfNtKm2)} — roughly half the Northern Territory — is Aboriginal freehold under a different statute.`,
    `${num(bandSquares)} of 1,000 squares. This band is a size, not a place: these registers do not record that land, so it cannot be mapped here.`,
    'Click to read the full caveat.',
  ));
  svg.append(band);

  frame.append(svg);
  attachSvgZoom(svg);

  /* ──────────────────────────── step machinery ──────────────────────────── */
  const catsFor = (step: number): Cat[] => {
    const out: Cat[] = new Array(TOTAL_SQUARES).fill('rest');
    if (step >= 3) {
      for (let i = 0; i < recSq; i++) out[i] = step >= 4 ? (i < exSq ? 'exclusive' : 'nonexclusive') : 'recognition';
    }
    if (step >= 5) {
      for (let i = 0; i < notExistSq; i++) out[SEPARATE_START + i] = 'notexist';
      for (let i = 0; i < extSq; i++) out[SEPARATE_START + notExistSq + i] = 'extinguished';
    }
    return out;
  };

  const legendFor = (step: number): { cat: Cat; count: number }[] => {
    if (step <= 2) return [{ cat: 'rest', count: TOTAL_SQUARES }];
    if (step === 3) return [{ cat: 'recognition', count: recSq }, { cat: 'rest', count: TOTAL_SQUARES - recSq }];
    if (step === 4) {
      return [
        { cat: 'exclusive', count: exSq },
        { cat: 'nonexclusive', count: nonExSq },
        { cat: 'rest', count: TOTAL_SQUARES - recSq },
      ];
    }
    return [
      { cat: 'exclusive', count: exSq },
      { cat: 'nonexclusive', count: nonExSq },
      { cat: 'notexist', count: notExistSq },
      { cat: 'extinguished', count: extSq },
      { cat: 'rest', count: restSq },
    ];
  };

  const captionFor = (step: number): string => {
    switch (step) {
      case 1:
        return `<h2>${esc(STEP_TITLES[0])}</h2>
          <p>Australia’s land area is <strong>${esc(num(meta.australiaLandKm2))} km²</strong>, as published by
            Geoscience Australia. That is the denominator for every percentage on this site and it never
            changes. Each of the thousand squares is ${esc(km2(unitKm2))}.</p>
          <p>What you are about to see comes from seven public registers kept by the National Native Title
            Tribunal: what the Federal Court has decided about native title, who holds it, what has been
            agreed over it, and what is still being argued. Sea country is excluded from every percentage
            here — the denominator is land, so a
            ${glossTerm('sea-determination', 'sea determination')} never moves these squares.</p>`;
      case 2:
        return `<h2>${esc(STEP_TITLES[1])}</h2>
          <p>There have been <strong>${esc(num(meta.counts.determinations))}</strong>
            ${glossTerm('determination', 'determinations')} since
            ${esc(longDate(meta.firstDetermination))} — the date of the first one on the register, the day
            the High Court handed down <em>Mabo v Queensland (No 2)</em>. The most recent is
            ${esc(longDate(meta.latestDetermination))}.</p>
          <p><strong>A determination is not automatically a recognition.</strong>
            ${esc(num(noneCount))} of them (${esc(pct(nonePct))}) found that
            ${glossTerm('does-not-exist', 'native title does not exist')} in the area the court considered,
            and many of the rest recognised it over only part of the area they were asked about.
            ${esc(num(consentCount))} (${esc(pct(consentPct))}) were reached by
            ${glossTerm('consent')} rather than argued out at trial.</p>
          <p class="small muted">Nothing on the grid has changed yet, deliberately. A count of court
            decisions tells you nothing at all about how much ground they cover: the land determination
            areas on this register run from ${esc(smallestLabel)} to
            ${esc(km2(largestArea, { exact: true }))}.</p>`;
      case 3:
        return `<h2>${esc(STEP_TITLES[2])}</h2>
          <p><strong>${esc(num(recSq))} of the thousand squares.</strong> Australian courts have recognised
            native title over ${esc(km2(meta.areas.recognisedKm2))} of land —
            <strong>${esc(pct(meta.areas.recognisedPct))}</strong> of the continent. That is the single
            largest fact on this site, and it is the block you are looking at.</p>
          <p>It is measured as one dissolved shape, not as a column of numbers added together, so no piece
            of country is counted twice. The next step divides it by what the recognition actually
            allows.</p>`;
      case 4:
        return `<h2>${esc(STEP_TITLES[3])}</h2>
          <p>The same block, split. <strong>${esc(pct(meta.areas.exclusiveShareOfRecognised))} of what was
            recognised is ${glossTerm('exclusive-possession', 'exclusive possession')}</strong> —
            ${esc(km2(meta.areas.exclusiveKm2))}, ${esc(num(exSq))} squares, and
            ${esc(pct(meta.areas.exclusivePct))} of Australia. It is the strongest form the law recognises:
            the right to possess the area against everyone else.</p>
          <p>The remaining ${esc(num(nonExSq))} squares — ${esc(km2(meta.areas.nonExclusiveKm2))},
            ${esc(pct(nonExPct))} of Australia — are ${glossTerm('non-exclusive', 'non-exclusive rights')}:
            real rights to hunt, fish, camp, hold ceremony and protect places, coexisting with someone
            else’s interest in the same ground, most often a pastoral lease.</p>
          <p class="small muted">Both halves are the same hue because both are recognition; they separate by
            depth and by texture, never by colour alone. Square counts are rounded to the nearest whole
            square — the percentages beside them are exact.</p>`;
      case 5:
        return `<h2>${esc(STEP_TITLES[4])}</h2>
          <p>Below the gap sits a second, separate block: ${esc(num(notExistSq))} squares where a court
            found ${glossTerm('does-not-exist', 'native title does not exist')}
            (${esc(km2(meta.areas.notExistKm2))}, ${esc(pct(notExistPct))} of Australia) and
            ${esc(num(extSq))} squares where it has been
            ${glossTerm('extinguishment', 'extinguished')} (${esc(km2(meta.areas.extinguishedKm2))},
            ${esc(pct(extPct))}).</p>
          <p><strong>These are not the remainder of the continent, and the four categories do not partition
            Australia.</strong> They are findings about specific areas — very often areas inside the same
            determination as recognised country, because a determination area is a mosaic: a road reserve,
            a town lot or an old freehold grant carved out of a much larger recognised block. They are drawn
            apart from the recognition block for that reason, and the gap between them is not spare
            ground.</p>
          <p class="small muted">The ${esc(num(restSq))} pale squares are neither: about
            ${esc(km2(restKm2))} that no outcome polygon on these registers touches at all. Some of it has
            never been the subject of an application; some of it is still being decided.</p>`;
      default:
        return `<h2>${esc(STEP_TITLES[5])}</h2>
          <p>The dashed band across the grid is ${esc(num(bandSquares))} squares —
            about ${esc(km2(halfNtKm2))}, roughly half the Northern Territory. It is a size, not a place,
            and nothing on these registers measures it.</p>
          <div class="note legal">${esc(ALRA_CAVEAT)}</div>
          <p class="small">On this register native title is recognised over
            ${esc(pct(nt?.recognisedPct ?? 0))} of the Northern Territory
            (${esc(km2(nt?.recognisedKm2 ?? 0))}) — which is why the Territory can look modest beside
            Western Australia’s ${esc(pct(jurisdictions.find((j) => j.code === 'WA')?.recognisedPct ?? 0))}
            and mean something quite different on the ground.</p>
          <p><button type="button" class="btn btn-sm" id="start-alra-step">Read the full explanation</button></p>`;
    }
  };

  /* ─────────────────────────────── the panel ────────────────────────────── */
  function openAlra(): void {
    openOverlay({
      panelClass: 'modal',
      label: 'The Aboriginal Land Rights (Northern Territory) Act 1976',
      lockScroll: true,
      build(panel, close) {
        const head = document.createElement('div');
        head.className = 'overlay-head';
        const h = document.createElement('h2');
        h.textContent = 'What these registers do not cover';
        head.append(h, closeButton(close, 'Close this explanation'));

        const body = document.createElement('div');
        body.className = 'stack';
        body.innerHTML = `
          <div class="note legal">${esc(ALRA_CAVEAT)}</div>
          <div class="detail-block">
            <h3>Why the band is a size and not a place</h3>
            <p class="small">The band is drawn to the area of roughly half the Northern Territory — about
              ${esc(km2(halfNtKm2))}, ${esc(num(bandSquares))} of the thousand squares — and to no boundary
              at all. These registers do not record land held under the
              ${glossTerm('alra', 'Aboriginal Land Rights (Northern Territory) Act 1976')}, so this site
              cannot show you where it is. Drawing it as a shape would be an invention.</p>
          </div>
          <div class="detail-block">
            <h3>What the register does say about the Territory</h3>
            <p class="small">Native title is recognised over ${esc(pct(nt?.recognisedPct ?? 0))} of the
              Northern Territory — ${esc(km2(nt?.recognisedKm2 ?? 0))} across
              ${esc(num(nt?.determinations ?? 0))} determinations, of which
              ${esc(km2(nt?.exclusiveKm2 ?? 0))} is
              ${glossTerm('exclusive-possession', 'exclusive possession')}. Read on its own, that share
              understates Aboriginal landholding in the Territory very substantially, because the largest
              body of Aboriginal-held land there was never a native title question in the first place.</p>
            <p class="small muted">Aboriginal freehold under the 1976 Act is inalienable, it is held by land
              trusts on behalf of traditional owners, and it is generally a stronger form of title than
              native title. It sits under a separate statute with a separate process, and it appears on none
              of the seven registers this site reads.</p>
          </div>
          <div class="detail-actions">
            <button type="button" class="btn" id="start-alra-map">See where native title applies →</button>
            <button type="button" class="btn" id="start-alra-jur">Compare the states and territories →</button>
          </div>
          <div class="note legal xsmall">The boundaries on this site are indicative only. They are not
            cadastral or legal boundaries, they cannot be used to establish rights over any particular
            parcel of land or water, and nothing here is legal advice.</div>
        `;
        body.querySelector<HTMLButtonElement>('#start-alra-map')!
          .addEventListener('click', () => { close(); navigate('map'); });
        body.querySelector<HTMLButtonElement>('#start-alra-jur')!
          .addEventListener('click', () => { close(); navigate('jurisdictions'); });

        panel.append(head, body);
      },
    });
  }

  /* ─────────────────────────────── rendering ────────────────────────────── */
  const dotsHost = root.querySelector<HTMLElement>('#start-dots')!;
  const stepCount = root.querySelector<HTMLElement>('#start-step-count')!;
  const legendHost = root.querySelector<HTMLElement>('#start-legend')!;
  const captionHost = root.querySelector<HTMLElement>('#start-caption')!;
  const prevBtn = root.querySelector<HTMLButtonElement>('#start-prev')!;
  const nextBtn = root.querySelector<HTMLButtonElement>('#start-next')!;

  for (let s = 1; s <= 6; s++) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'start-dot mono';
    b.textContent = String(s);
    b.dataset.step = String(s);
    b.setAttribute('aria-label', `Step ${s} of 6: ${STEP_TITLES[s - 1]}`);
    b.setAttribute('data-tip', tip(`Step ${s} of 6`, STEP_TITLES[s - 1]));
    b.addEventListener('click', () => go(s));
    dotsHost.append(b);
  }

  let step = 1;

  function apply(): void {
    const cats = catsFor(step);
    const rest = restInfo(step);
    for (let i = 0; i < TOTAL_SQUARES; i++) {
      const c = cats[i];
      const p = PAINT[c];
      const r = cells[i];
      r.setAttribute('fill', p.fill);
      r.setAttribute('stroke', p.stroke);
      r.setAttribute('data-cat', c);
      r.setAttribute('data-tip', c === 'rest' ? rest.tip : p.tip);
      r.setAttribute('class', c === 'rest' ? 'start-cell' : 'start-cell start-on hit');
    }

    separator.style.display = step >= 5 ? '' : 'none';
    band.style.display = step >= 6 ? '' : 'none';

    stepCount.textContent = `Step ${step} of 6`;
    for (const dot of dotsHost.querySelectorAll<HTMLButtonElement>('.start-dot')) {
      if (Number(dot.dataset.step) === step) dot.setAttribute('aria-current', 'step');
      else dot.removeAttribute('aria-current');
    }

    legendHost.innerHTML = legendFor(step).map(({ cat, count }) => {
      const p = PAINT[cat];
      const tipText = cat === 'rest' ? rest.tip : p.tip;
      const label = cat === 'rest' ? rest.label : p.label;
      return `<span class="legend-item" data-tip="${tipAttr(tipText)}">
        <span class="legend-swatch" style="${esc(p.swatch)}"></span>
        <span>${esc(label)}</span>
        <span class="start-count mono">${esc(num(count))}/1,000</span>
      </span>`;
    }).join('') + (step >= 6 ? `<span class="legend-item" data-tip="${tipAttr(
      'Aboriginal Land Rights (Northern Territory) Act 1976',
      `About ${km2(halfNtKm2)}, roughly half the Northern Territory — not recorded on any of these registers.`,
      `${num(bandSquares)} of 1,000 squares. A size, not a place.`,
    )}">
      <span class="legend-swatch start-band-swatch"></span>
      <span>Not on these registers at all</span>
      <span class="start-count mono">${esc(num(bandSquares))}/1,000</span>
    </span>` : '');

    captionHost.innerHTML = captionFor(step);
    captionHost.querySelector<HTMLButtonElement>('#start-alra-step')?.addEventListener('click', openAlra);

    prevBtn.disabled = step === 1;
    nextBtn.disabled = step === 6;
  }

  function go(next: number): void {
    const clamped = Math.min(6, Math.max(1, next));
    if (clamped === step) return;
    step = clamped;
    apply();
  }

  prevBtn.addEventListener('click', () => go(step - 1));
  nextBtn.addEventListener('click', () => go(step + 1));
  root.querySelector<HTMLButtonElement>('#start-to-map')!.addEventListener('click', () => navigate('map'));
  root.querySelector<HTMLButtonElement>('#start-to-jur')!.addEventListener('click', () => navigate('jurisdictions'));
  root.querySelector<HTMLButtonElement>('#start-alra-note')!.addEventListener('click', openAlra);

  band.addEventListener('click', openAlra);
  band.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openAlra(); }
  });

  // Clicking any filled block opens the map. Pale squares are not styled as
  // interactive and do nothing, so there is no click that looks live and isn't.
  svg.addEventListener('click', (e) => {
    const target = (e.target as Element | null)?.closest?.('[data-cat]');
    if (!target) return;
    if (target.getAttribute('data-cat') === 'rest') return;
    navigate('map');
  });

  /**
   * ArrowLeft / ArrowRight. The listener is on the document so the keys work
   * without hunting for focus, and it removes itself the first time it fires
   * after this view has been detached — a listener that outlives its view would
   * keep stepping a grid the reader can no longer see.
   */
  const onKey = (e: KeyboardEvent) => {
    if (!root.isConnected) { document.removeEventListener('keydown', onKey); return; }
    if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
    const t = e.target as Element | null;
    if (t?.closest?.('input, textarea, select, [contenteditable="true"], .overlay-host')) return;
    if (e.key === 'ArrowRight') { e.preventDefault(); go(step + 1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); go(step - 1); }
  };
  document.addEventListener('keydown', onKey);

  /* ───────────────────────── what the words mean ────────────────────────── */
  const wordsHost = root.querySelector<HTMLElement>('#start-words')!;
  wordsHost.innerHTML = TENURE_ORDER.map((t) => {
    const style = TENURE[t];
    const key = GLOSS_KEY[t];
    const area = t === 'exclusive' ? meta.areas.exclusiveKm2
      : t === 'nonexclusive' ? meta.areas.nonExclusiveKm2
        : t === 'notexist' ? meta.areas.notExistKm2 : meta.areas.extinguishedKm2;
    const squares = t === 'exclusive' ? exSq : t === 'nonexclusive' ? nonExSq : t === 'notexist' ? notExistSq : extSq;
    return `<button type="button" class="legend-card" data-term="${esc(key)}"
        data-tip="${tipAttr(style.label, `${km2(area, { exact: true })} · ${num(squares)} of 1,000 squares`, 'Click for the full definition.')}">
      <span class="legend-swatch" style="${esc(PAINT[t].swatch)}"></span>
      <span class="lc-body">
        <span class="lc-name">${esc(style.label)}</span>
        <span class="lc-num"> · ${esc(km2(area))}</span>
        <span class="lc-plain">${esc(style.plain)}</span>
        <span class="lc-plain start-register">Register’s own words: “${esc(style.register)}”</span>
      </span>
    </button>`;
  }).join('');

  apply();
  host.append(root);
}
